import { Influencer } from '../model/InfluencerList.types';
import { FilterFieldKey, FilterRule } from './InfluencerList.filterTypes';
import { toDateRange } from './InfluencerList.viewModel.mappers';

const getFieldValue = (
  influencer: Influencer,
  field: FilterFieldKey,
): string | number | boolean | null => influencer[field] ?? null;

const applyNumericFilter = (
  fieldValue: number,
  operator: string,
  ruleValue: number,
): boolean => {
  switch (operator) {
    case 'eq':
      return fieldValue === ruleValue;
    case 'neq':
      return fieldValue !== ruleValue;
    case 'gt':
      return fieldValue > ruleValue;
    case 'lt':
      return fieldValue < ruleValue;
    default:
      return true;
  }
};

const applyStringFilter = (
  fieldValue: string,
  operator: string,
  ruleValue: string,
): boolean => {
  const normalizedField = fieldValue.toLowerCase();
  const normalizedRule = ruleValue.toLowerCase();

  switch (operator) {
    case 'eq':
      return normalizedField === normalizedRule;
    case 'neq':
      return normalizedField !== normalizedRule;
    case 'contains':
      return normalizedField.includes(normalizedRule);
    default:
      return true;
  }
};

const applyDateFilter = (
  fieldValue: number,
  operator: string,
  ruleValue: number | null,
  now: Date = new Date(),
): boolean => {
  switch (operator) {
    case 'last-n-days': {
      if (ruleValue == null) return true;
      const from = now.getTime() - ruleValue * 86_400_000;
      return fieldValue >= from && fieldValue < now.getTime();
    }
    case 'this-month': {
      const range = toDateRange('this-month', now);
      if (!range) return true;
      return fieldValue >= range.from && fieldValue < range.to;
    }
    case 'last-month': {
      const range = toDateRange('last-month', now);
      if (!range) return true;
      return fieldValue >= range.from && fieldValue < range.to;
    }
    case 'before':
      return ruleValue != null && fieldValue < ruleValue;
    case 'after':
      return ruleValue != null && fieldValue > ruleValue;
    default:
      return true;
  }
};

export const applyFilterRule =
  (rule: FilterRule, now: Date = new Date()) =>
  (influencer: Influencer): boolean => {
    const fieldValue = getFieldValue(influencer, rule.field);

    if (rule.operator === 'exists') return fieldValue != null;
    if (rule.operator === 'not-exists') return fieldValue == null;

    if (fieldValue == null) return false;

    if (rule.operator === 'this-month' || rule.operator === 'last-month') {
      return applyDateFilter(fieldValue as number, rule.operator, null, now);
    }

    if (rule.value == null) return true;

    if (typeof fieldValue === 'number' && typeof rule.value === 'number') {
      if (
        rule.operator === 'last-n-days' ||
        rule.operator === 'before' ||
        rule.operator === 'after'
      ) {
        return applyDateFilter(fieldValue, rule.operator, rule.value, now);
      }

      return applyNumericFilter(fieldValue, rule.operator, rule.value);
    }

    if (typeof fieldValue === 'string' && typeof rule.value === 'string') {
      return applyStringFilter(fieldValue, rule.operator, rule.value);
    }

    if (rule.operator === 'eq')
      return String(fieldValue) === String(rule.value);
    if (rule.operator === 'neq')
      return String(fieldValue) !== String(rule.value);

    return true;
  };

export const applyAllFilterRules = (
  influencers: Influencer[],
  rules: FilterRule[],
  now: Date = new Date(),
): Influencer[] =>
  rules.reduce(
    (acc, rule) => acc.filter(applyFilterRule(rule, now)),
    influencers,
  );
